import React from "react"
import { cn, formatCurrency } from "../utils"

export interface ContractConfig {
  monthly_price: number // in cents
  commission_percent: number
}

export interface InvitationData {
  client_name: string
  logo_s3_url: string
  contract_config: ContractConfig
}

interface ClientInvitationProps {
  data: InvitationData
  onAcceptACH?: (routingNumber: string, accountNumber: string) => void
  onAcceptStripe?: () => void
  className?: string
  showPaymentOptions?: boolean
}

export function ClientInvitation({
  data,
  onAcceptACH,
  onAcceptStripe,
  className,
  showPaymentOptions = true,
}: ClientInvitationProps) {
  const [paymentMethod, setPaymentMethod] = React.useState<"ach" | "stripe" | null>(null)
  const [routingNumber, setRoutingNumber] = React.useState("")
  const [accountNumber, setAccountNumber] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  function handleSubmitACH(e: any) {
    e.preventDefault()
    if (onAcceptACH && routingNumber && accountNumber) {
      setIsSubmitting(true)
      onAcceptACH(routingNumber, accountNumber)
    }
  }

  function handleStripePayment() {
    if (onAcceptStripe) {
      setIsSubmitting(true)
      onAcceptStripe()
    }
  }

  return (
    <div className={cn("max-w-2xl mx-auto p-8", className)}>
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Header Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 text-center border-b border-gray-100">
          <div className="mb-6 flex justify-center">
            <div className="w-32 h-32 bg-white rounded-xl shadow-md flex items-center justify-center p-4">
              <img
                src={data.logo_s3_url}
                alt={`${data.client_name} logo`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to MarginWiz!</h1>
          <p className="text-lg text-gray-600">
            We're excited to partner with <span className="font-semibold text-gray-900">{data.client_name}</span>
          </p>
        </div>

        {/* Pricing Section */}
        <div className="p-8 space-y-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Plan</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Monthly Subscription</span>
                <span className="text-2xl font-bold text-gray-900">
                  {formatCurrency(data.contract_config.monthly_price)}
                </span>
              </div>
              {data.contract_config.commission_percent > 0 && (
                <div className="flex justify-between items-center pt-3 border-t border-green-200">
                  <span className="text-gray-700">Success Fee</span>
                  <span className="text-xl font-semibold text-gray-900">
                    {data.contract_config.commission_percent}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Options */}
          {showPaymentOptions && (
            <>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Choose Payment Method</h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("ach")}
                    disabled={isSubmitting}
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md",
                      paymentMethod === "ach"
                        ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                        : "border-gray-200 hover:border-gray-300",
                      isSubmitting && "opacity-50 cursor-not-allowed"
                    )}>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-900">Bank Account (ACH)</div>
                      <div className="text-sm text-gray-600 mt-1">Lower fees</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("stripe")}
                    disabled={isSubmitting}
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md",
                      paymentMethod === "stripe"
                        ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                        : "border-gray-200 hover:border-gray-300",
                      isSubmitting && "opacity-50 cursor-not-allowed"
                    )}>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-900">Credit Card</div>
                      <div className="text-sm text-gray-600 mt-1">Via Stripe</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* ACH Form */}
              {paymentMethod === "ach" && (
                <form onSubmit={handleSubmitACH} className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <div>
                    <label htmlFor="routing" className="block text-sm font-medium text-gray-700 mb-2">
                      Routing Number
                    </label>
                    <input
                      id="routing"
                      type="text"
                      value={routingNumber}
                      onChange={e => setRoutingNumber((e.target as HTMLInputElement).value)}
                      placeholder="123456789"
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      maxLength={9}
                      pattern="[0-9]{9}"
                    />
                  </div>
                  <div>
                    <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number
                    </label>
                    <input
                      id="account"
                      type="text"
                      value={accountNumber}
                      onChange={e => setAccountNumber((e.target as HTMLInputElement).value)}
                      placeholder="Account number"
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? "Processing..." : "Complete Setup"}
                  </button>
                </form>
              )}

              {/* Stripe Option */}
              {paymentMethod === "stripe" && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    You'll be redirected to Stripe to securely add your credit card information.
                  </p>
                  <button
                    type="button"
                    onClick={handleStripePayment}
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? "Redirecting..." : "Continue with Stripe"}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Benefits Section */}
          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What you'll get:</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-500 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Automated deduction management and tracking</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-500 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Direct dispute submission to distributors</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-500 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Real-time insights and analytics</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-500 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Dedicated support team</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
